import { Button, Select } from 'antd'
import { LogBean } from 'common/api/api'
import { logApi } from 'common/api/entitiesApi.tsx'
import { ListCard } from 'common/components/ListCard'
import { SearchInput } from 'common/components/SearchInput'
import { ListFilters, useAppContext } from 'common/context/AppContext'
import { initListFilters, useListFilters } from 'common/util/useListFilters'
import { apiCall } from 'common/util/Util'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const { Option } = Select

const LogList = ({admin}) => {
  const appContext = useAppContext()
  const { entity: entityParam, id } = useParams<{ entity?: string; id?: string }>()
  const [filters, setFilters] = useState<ListFilters>(initListFilters())
  const { setFilter } = useListFilters(setFilters)
  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<any>()

  const wrapStyle: React.CSSProperties = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  }

  useEffect(() => {
    if (entityParam) {
      setFilter('type', entityParam)
    }
  }, [entityParam])

  const loadData = () => {
    const size = filters.pageSize ?? 10
    const page = filters.page ?? 1

    apiCall(
      setLoading,
      appContext,
      () =>
        logApi.logList(filters.sort?.name === 'modified' ? 'id' : filters.sort?.name, filters.sort?.direction, filters.name, filters.type, {
          query: { size, page },
        }),
      (data) => setListData(data),
    )
  }

  useEffect(() => {
    loadData()
  }, [filters, id])

  const valueRender = useCallback(
    (value: unknown): React.ReactNode => (
      <span>{typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value)}</span>
    ),
    [],
  )

  const jsonRender = useCallback((json: Record<string, any>): React.ReactNode => {
    return (
        <div style={wrapStyle}>
        {Object.keys(json).map((k) => {
          const value = json[k]
          return (
              <div key={k} style={wrapStyle}>
              {k}: {value && typeof value === 'object' ? jsonRender(value) : valueRender(value)}
            </div>
          )
        })}
      </div>
    )
    },
    [valueRender],
  )

  const prettify = useCallback(
    (value: string): React.ReactNode => {
      try {
        const json = JSON.parse(value || '{}')
        return jsonRender(json)
      } catch {
        return <span style={wrapStyle}>Invalid JSON format</span>
      }
    },
    [jsonRender],
  )

  const handleDownload = () => {
    const entity = (filters as any).entity || entityParam
    if (!entity) return

    apiCall(
      setLoading,
      appContext,
      () => logApi.getLogCsv(entity),
      (result: { csv: string }) => {
        const blob = new Blob([result.csv], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'log.csv'
        a.click()
        URL.revokeObjectURL(url)
      },
    )
  }

  return (
    <ListCard<LogBean>
      loading={loading}
      title="Change Logs"
      filters={filters}
      setFilters={setFilters}
      listData={listData}
      loadData={loadData}
      columns={[
        {
          title: 'Entity Type',
          dataIndex: 'entity',
          width: 140,
        },
        {
          title: 'Entity Name',
          dataIndex: 'name',
          className: 'list-table-name',
          ellipsis: true,
          width: 260,
        },
        {
          title: 'Account',
          dataIndex: 'account',
          width: 160,
        },
        {
          title: 'Prev Value',
          render: (_: any, record: LogBean) => (
            <div style={wrapStyle}>{prettify(record.prevValue || '')}</div>
          ),
        },
        {
          title: 'Current Value',
          render: (_: any, record: LogBean) => (
            <div style={wrapStyle}>{prettify(record.currentValue || '')}</div>
          ),
        },
        {
          title: 'Date/Time',
          dataIndex: 'date',
          width: 180,
        },
      ]}
    >
      <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)} />
      <Select
        placeholder="Entity type"
        allowClear
        style={{ minWidth: 160 }}
        value={filters.type}
        onChange={(val) => setFilter('type', val)}
      >
        {admin && <Option value={LogBean.EntityEnum.Agency}>Agency</Option>}
        <Option value={LogBean.EntityEnum.Advertiser}>Advertisers</Option>
        <Option value={LogBean.EntityEnum.Campaign}>Campaigns</Option>
        <Option value={LogBean.EntityEnum.AdGroup}>AdGroups</Option>
        <Option value={LogBean.EntityEnum.Creative}>Creatives</Option>
        <Option value={LogBean.EntityEnum.Dsp}>DSP</Option>
      </Select>

      <Button onClick={handleDownload} disabled={!filters.type}>
        Download
      </Button>
    </ListCard>
  )
}

export default LogList
